import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Input,
  CircularProgress,
  Divider,
  Snackbar,
  Alert
} from "@mui/material";
import { useSelector } from "react-redux";
import { investorApi } from "../../../api/api";
import { useLocation, useNavigate } from "react-router-dom";
import GradientButton from "../../common/GradiantButton";

const KYCNew = () => {
  const [panCardNumber, setPanCardNumber] = useState("");
  const [adhaarNumber, setAdhaarNumber] = useState("");
  const [panCard, setPanCard] = useState(null);
  const [aadhaarCard, setAadhaarCard] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" // can be "success", "error", "warning", "info"
  });

  const userId = useSelector((data) => data.user.userId);
  const theme = useSelector((data) => data.themeValue.colors);
  const themeMode = useSelector((data) => data.themeValue.theme);
  const aadhaarRegex = /^(?:\d{4}\s\d{4}\s\d{4}|\d{12})$/;
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
  const navigate = useNavigate();
  const handleFileSelect = (e, docType) => {
    const file = e.target.files[0];
    if (!file) return;

    const isPdf = file.type === "application/pdf";
    const isImage = file.type.startsWith("image/");

    if (!isPdf && !isImage) {
      setSnackbar({ open: true, message: "Only image or PDF files are allowed." });
      return;
    }

    const fileData = {
      file,
      url: URL.createObjectURL(file),
      type: file.type,
      name: file.name,
    };

    if (docType === "pan") setPanCard(fileData);
    if (docType === "aadhaar") setAadhaarCard(fileData);
  };

  const validateInputs = () => {
    if (!panCardNumber || !adhaarNumber || !panCard || !aadhaarCard) {
      alert("Please fill all fields and upload both documents.");
      return false;
    }

    // if (!panRegex.test(panCardNumber)) {
    //   alert("Invalid PAN Number. Please check your PAN number.");
    //   return false;
    // }

    // if (!aadhaarRegex.test(adhaarNumber)) {
    //   alert("Invalid Aadhaar Number. Please check your Aadhaar number.");
    //   return false;
    // }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateInputs()) return;

    const formData = new FormData();
    formData.append("userId", userId);
    formData.append("pancardNumber", panCardNumber);
    formData.append("adhaarNumber", adhaarNumber);
    formData.append("pancard", panCard.file);
    formData.append("adhaar", aadhaarCard.file);

    setUploading(true);
    try {
      const response = await investorApi.uploadKyc(formData);
      if (response.status === 200) {
        setSnackbar({
          open: true,
          message: "KYC submitted successfully!",
          severity: "success"
        });

        setTimeout(() => {
          navigate("/home");
        }, 1500);

      }


      else {
        setSnackbar({
          open: true,
          message: "Error during submission.",
          severity: "error"
        });
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Error during submission." });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Paper
      elevation={4}
      sx={{
        p: 4,
        borderRadius: 4,
        maxWidth: 600,
        mx: "auto",
        mt: 5,
        background: theme.background,
        boxShadow: themeMode === "light" ? "0px 4px 20px rgba(0, 0, 0, 0.1)" : "0px 4px 20px rgba(255, 255, 255, 0.1)",
      }}
    >
      <Typography
        variant="h5"
        fontWeight="bold"
        textAlign="center"
        gutterBottom
        sx={{ color: theme.text }}
      >
        Upload KYC Documents
      </Typography>

      <Divider sx={{ mb: 4, backgroundColor: theme.secondary_text_color }} />

      <TextField
        label="PAN Card Number"
        variant="outlined"
        fullWidth
        value={panCardNumber}
        onChange={(e) => setPanCardNumber(e.target.value)}
        sx={{
          mb: 3,
          input: {
            color: theme.text,
          },
          label: {
            color: theme.text,
          },
          '& label.Mui-focused': {
            color: theme.primary_color,
          },
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: theme.text,
            },
            '&:hover fieldset': {
              borderColor: theme.primary_color,
            },
            '&.Mui-focused fieldset': {
              borderColor: theme.primary_color,
            },
          },
        }}
      />

      <Button
        component="label"
        variant="outlined"
        fullWidth
        sx={{
          mb: 2,
          py: 1.2,
          textTransform: "none",
          fontWeight: "bold",
        }}
      >
        Upload PAN Card (PDF/Image)
        <Input
          type="file"
          hidden
          onChange={(e) => handleFileSelect(e, "pan")}
          accept="image/*,.pdf"
        />
      </Button>

      {panCard && (
        <Box sx={{ mb: 3 }}>
          {panCard.type === "application/pdf" ? (
            <a href={panCard.url} target="_blank" rel="noopener noreferrer">
              View PAN PDF
            </a>
          ) : (
            <Box
              component="img"
              src={panCard.url}
              alt="PAN Preview"
              sx={{
                width: "100%",
                height: 200,
                objectFit: "contain",
                borderRadius: 2,
                border: "1px solid #ccc",
                boxShadow: 1,
              }}
            />
          )}
        </Box>
      )}

      <TextField
        label="Aadhaar Number"
        variant="outlined"
        fullWidth
        value={adhaarNumber}
        onChange={(e) => setAdhaarNumber(e.target.value)}
        sx={{
          mb: 3,
          input: {
            color: theme.text,
          },
          label: {
            color: theme.text,
          },
          '& label.Mui-focused': {
            color: theme.primary_color,
          },
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: theme.text,
            },
            '&:hover fieldset': {
              borderColor: theme.primary_color,
            },
            '&.Mui-focused fieldset': {
              borderColor: theme.primary_color,
            },
          },
        }}
      />

      <Button
        component="label"
        variant="outlined"
        fullWidth
        sx={{
          mb: 2,
          py: 1.2,
          textTransform: "none",
          fontWeight: "bold",
        }}
      >
        Upload Aadhaar Card (PDF/Image)
        <Input
          type="file"
          hidden
          onChange={(e) => handleFileSelect(e, "aadhaar")}
          accept="image/*,.pdf"
        />
      </Button>

      {aadhaarCard && (
        <Box sx={{ mb: 3 }}>
          {aadhaarCard.type === "application/pdf" ? (
            <a href={aadhaarCard.url} target="_blank" rel="noopener noreferrer">
              View Aadhaar PDF
            </a>
          ) : (
            <Box
              component="img"
              src={aadhaarCard.url}
              alt="Aadhaar Preview"
              sx={{
                width: "100%",
                height: 200,
                objectFit: "contain",
                borderRadius: 2,
                border: "1px solid #ccc",
                boxShadow: 1,
              }}
            />
          )}
        </Box>
      )}
      <GradientButton
        text={uploading ? "Submitting..." : "Submit KYC"}
        width="100%"
        onClick={handleSubmit} />


      {uploading && (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      )}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >

          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default KYCNew;
